import { ChangeDetectorRef, Component, computed, inject, input, output, OnDestroy, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { RegimenClass } from '@cureskin/api-client/src/server-api';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { EventLoggerService } from '@services/event-logger-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { RegimenService } from '@services/regimen-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { CurrentComponentService } from '@services/current-component';
import { ReportModule } from 'client/app/report/report.module';
import { ButtonModule } from '@components/button';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { CsIconModule } from '@components/icon';
import { AllocatedRegimen } from '../../../../app-constant-types';
import { AppConfig } from '../../../../app.config';
import { REGIMEN_ROUTE_PATHS, REGIMEN_QUERY_PARAMS } from '../regimen.constants';

@Component({
  selector: 'user-view-regimen-preparation',
  templateUrl: './user-view-regimen-preparation.html',
  imports: [CommonModule, ReportModule, ButtonModule, ButtonCtaComponent, CsIconModule],
})
export class UserViewRegimenPreparationComponent implements OnDestroy, OnInit {

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly experimentService = inject(ExperimentService);
  private readonly currentComponentService = inject(CurrentComponentService);
  private readonly conn = inject(ConnectionService);
  public readonly  appConfig = inject(AppConfig);
  private readonly broadcast = inject(BroadcastService);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly changeDetectionRef = inject(ChangeDetectorRef);
  private readonly appWebBridge = inject(AppWebBridgeService);
  private readonly regimenService = inject(RegimenService);


  regimen = input<AllocatedRegimen>();
  private readonly fetchedRegimen = signal<AllocatedRegimen | null>(null);
  protected readonly regimenData = computed<AllocatedRegimen | null>(
    () => this.fetchedRegimen() ?? this.regimen() ?? null,
  );
  showRegimen = output<void>();
  hideToolBars = output<void>();
  isRouteMode = false;
  user: any;
  showPreparationTimer: boolean = false;
  timer: any = { minutes: 0, seconds: 0 };
  interval: any = {};
  primaryConcern: string = '';
  otherConcerns: string = '';
  concerns: Array<any> = [];
  defaultTime: number = 120_000;
  elapsedTime: number = 0;
  isPaidUser: boolean = false;
  showRegimenToast: boolean = false;
  remTime: number;
  startTimer: number;
  regimenPreparationTimerVariant: {
    activationDelayInSeconds: number,
  } = {
    activationDelayInSeconds: 120
  };
  TIMER_MESSAGE_KEYS = {
    GETTING_TREATMENT_PLAN_READY: 'gettingTreatmentPlanReady',
    MATCHING_BEST_INGREDIENTS: 'matchingBestIngredients',
    FINDING_RIGHT_PRODUCTS: 'findingRightProducts',
    ADDING_FACE_WASH: 'addingFaceWash',
    ADDING_SUNSCREEN_MOISTURISER: 'addingSunscreenMoisturiser',
    ADDING_REPAIR_FOR_CONCERN: 'addingRepairForConcern',
    FINDING_BEST_SUPPLEMENT: 'findingBestSupplement',
    ALMOST_DONE: 'almostDone'
  } as const;
  readonly TIMER_MESSAGE_VALUES = Object.values(this.TIMER_MESSAGE_KEYS);
  private readonly MILLISECONDS_PER_SECOND = 1000;
  private readonly SECONDS_PER_MINUTE = 60;

  async ngOnInit(): Promise<any> {
    this.isRouteMode = this.activatedRoute.snapshot.routeConfig?.path === REGIMEN_ROUTE_PATHS.PREPARATION;
    if (this.isRouteMode) {
      this.currentComponentService.set(this);
      await this.initRouteMode();
    }
    await this.experimentService.experimentsLoaded;
    this.applyExperimentFlags();
    this.startTimer = new Date().getTime();
    this.user = await this.conn.getActingUser();
    if (!this.user?.get('allocatedDoctor')) this.user = await this.conn?.findUserByObjectId(this.user?.id);
    await this.showRegimenTimer();
    this.extractConcernsAndLogPageOpen();
    if (!this.isRouteMode) this.hideToolBars.emit();
    this.updatePaidUserFlag();
    this.changeDetectionRef.detectChanges();
    const time = new Date().getTime() - this.startTimer;
    this.eventLogger.trackEvent('Timer_screen_loading_time', { timeInMilliSec: time, userId: this.user.id });
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'pageOpenRegimenTimer' },
      firebase: { name: 'pageOpenRegimenTimer' },
    });
  }

  /** Route mode: hydrate the regimen for the ?class from RegimenService. */
  private async initRouteMode(): Promise<void> {
    const regimenClass = (this.activatedRoute.snapshot.queryParams[REGIMEN_QUERY_PARAMS.CLASS] as RegimenClass)
      || this.appConfig.Shared.Regimen.Class.FACE;
    const regimens = await this.regimenService.getRegimens();
    const regimen = regimens.find((regimen: AllocatedRegimen): boolean => regimen.class === regimenClass) ?? null;
    this.fetchedRegimen.set(regimen);
  }

  private applyExperimentFlags(): void {
    this.regimenPreparationTimerVariant = this.experimentService.regimenPreparationTimerVariant;
    if (this.experimentService.regimenRehaulV2) this.regimenService.setRegimenVariantImages(this.regimenData(), false);
  }

  extractConcernsAndLogPageOpen(): void {
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'regimen-preparation-new-ui', regimenClass: this.regimenData()?.class }));
    this.concerns = this.user?.get('aiDetections');
    if (this.concerns?.length) {
      this.primaryConcern = this.concerns[0];
      this.otherConcerns = this.concerns.slice(1).join(', ');
    }
  }

  updatePaidUserFlag(): void {
    this.isPaidUser = [
      this.appConfig.Shared.User.OrderState.PROCESSING,
      this.appConfig.Shared.User.OrderState.DELIVERED,
    ].includes(this.user.get('orderState'));
  }

  async showRegimenTimer(): Promise<any> {
    this.defaultTime = (this.regimenPreparationTimerVariant?.activationDelayInSeconds || 120) * 1000;
    const currTime: any = new Date();
    const regimenShowTime: any = new Date(this.regimenData()?.activeFrom?.iso ?? '');
    this.remTime = regimenShowTime - currTime;
    this.elapsedTime = -(this.defaultTime - (regimenShowTime - currTime));
    if (this.elapsedTime >= this.defaultTime) this.elapsedTime = -this.defaultTime;
    setTimeout((): void => this.startRegimenTimer((regimenShowTime - currTime)));
  }
  // Calculate remaining minutes and seconds from milliseconds
  calculateRemainingTime(milliseconds: number): { minutes: number; seconds: number } {
    const minutes = Math.floor(milliseconds / 1000 / 60);
    const seconds = Math.floor((milliseconds - (minutes * 1000 * 60)) / 1000);
    return { minutes, seconds };
  }

  // Start animation after a delay
  startAnimationAfterDelay(delay: number): void {
    setTimeout((): boolean => this.timer.animate = true, delay);
  }

  // Update the timer and check if it's finished
  updateTimerAndCheckIfFinished(): void {
    if (this.timer.seconds === 0) {
      this.timer.seconds = 59;
      this.timer.minutes -= 1;
    } else {
      this.timer.seconds -= 1;
    }

    // Trigger change detection to update dynamic message
    this.changeDetectionRef.detectChanges();

    if (this.timer.minutes === 0 && this.timer.seconds === 0) {
      clearInterval(this.interval.timer);
      this.showRegimenToast = true;
      this.changeDetectionRef.detectChanges();
    }
  }

  showRegimenPage(fromToast: boolean = false): void {
    if (fromToast) this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'report-toast-view' }));
    if (this.isRouteMode) {
      const regimenClass = this.regimenData()?.class;
      void this.router.navigate(['/user'], { queryParams: { tab: 'regimen', class: regimenClass }, replaceUrl: true });
      return;
    }
    this.showRegimen.emit();
  }

  // Start the timer and update it at regular intervals
  startIntervalTimer(updateInterval: number): void {
    this.interval.timer = setInterval(this.updateTimerAndCheckIfFinished.bind(this), updateInterval);
  }

  // Main function for starting the regimen timer
  startRegimenTimer(milliseconds: number = 0): void {
    if (milliseconds < 0) return;

    const timeToUse = Math.min(milliseconds, this.defaultTime);
    const { minutes, seconds }: { minutes: number, seconds: number } = this.calculateRemainingTime(timeToUse);
    this.timer.minutes = minutes;
    this.timer.seconds = seconds;

    // Recalculate elapsedTime for animation synchronization based on actual remaining time
    this.elapsedTime = -(this.defaultTime - timeToUse);
    if (this.elapsedTime >= this.defaultTime) this.elapsedTime = -this.defaultTime;

    const isTimeOver = this.timer.minutes === 0 && this.timer.seconds === 0;
    if (!isTimeOver) {
      this.startAnimationAfterDelay(500);
      this.startIntervalTimer(1000);
    }

    this.changeDetectionRef.detectChanges();
  }

  async handleBackPress(): Promise<boolean> {
    return this.router.navigate(['/user'], { queryParams: { tab: 'home', init: 'true' }, replaceUrl: true });
  }

  back(): void {
    if (this.isRouteMode) {
      this.handleBackPress().catch((): void => undefined);
      return;
    }
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  get showDynamicTimerContent(): boolean {
    return this.regimenData()?.class === this.appConfig.Shared.Regimen.Class.FACE;
  }

  getCurrentMessageKey(): string {
    const totalSeconds = this.defaultTime / this.MILLISECONDS_PER_SECOND;
    const remainingSeconds = (this.timer.minutes * this.SECONDS_PER_MINUTE) + this.timer.seconds;
    const elapsedSeconds = totalSeconds - remainingSeconds;
    const phaseDuration = totalSeconds / this.TIMER_MESSAGE_VALUES.length;
    const currentPhase = Math.min(Math.floor(elapsedSeconds / phaseDuration), this.TIMER_MESSAGE_VALUES.length - 1);
    return this.TIMER_MESSAGE_VALUES[currentPhase];
  }

  ngOnDestroy(): void {
    clearInterval(this.interval.timer);
    clearInterval(this.interval.carousel);
    if (this.isRouteMode) this.currentComponentService.remove(this);
  }
}
